import React, { useMemo, useState } from "react";
import { Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";

type Props = {
  onClose: () => void;
  goBack: () => void;
  teamDisplayName: string | null;
  userIdTeam: string | null;
  rostersData: any;
};

const RostersTeamView: React.FC<Props> = ({ onClose, goBack, teamDisplayName, userIdTeam, rostersData }) => {
  const selectedCompetitorTeam = rostersData?.competitor_teams?.find((team) => team.team_id === userIdTeam);
  const isMyTeamSelected = userIdTeam === "MY_TEAM"; // i
  const players = isMyTeamSelected ? rostersData?.user_team?.players : selectedCompetitorTeam?.players;
  const picks = isMyTeamSelected ? rostersData?.user_team?.draft_picks : selectedCompetitorTeam?.draft_picks;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [valueSortDirection, setValueSortDirection] = useState<"desc" | "asc" | null>(null);

  const sortedRows = useMemo(() => {
    const rows = [
      ...(players ?? []).map((player) => ({ kind: "player" as const, data: player })),
      ...(picks ?? []).map((pick) => ({ kind: "pick" as const, data: pick })),
    ];

    if (valueSortDirection === null) {
      return rows;
    }

    const getSortValue = (row: (typeof rows)[number]) => (row.kind === "player" ? row.data.player_value : row.data.draft_pick_value);

    return [...rows].sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      const aIsInvalid = valueA == null || Number.isNaN(Number(valueA));
      const bIsInvalid = valueB == null || Number.isNaN(Number(valueB));
      if (aIsInvalid && bIsInvalid) return 0;
      if (aIsInvalid) return 1;
      if (bIsInvalid) return -1;
      return valueSortDirection === "desc" ? Number(valueB) - Number(valueA) : Number(valueA) - Number(valueB);
    });
  }, [players, picks, valueSortDirection]);

  const handleValueSort = () => {
    setValueSortDirection((prev) => (prev === null ? "desc" : prev === "desc" ? "asc" : "desc"));
  };

  const getNameWithSuspensivePoints = (fullName: string) => {
    if (fullName?.length > 17 && isMobile) {
      return `${fullName.substring(0, 17)}...`;
    }
    return fullName;
  };

  // helper to format numeric values similar to trade view
  const formatValue = (value: number | string | undefined | null) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "";
    }
    // Function to format the value to 2 decimal place
    const formatted = Number(value).toFixed(2);
    return formatted === "0.00" ? "0" : formatted;
  };

  //Information partners for the 6 cards from teamsTrade
  const informationPartners = [
    { text: `${rostersData?.league_info?.league_size || "-"} Teams` },
    { text: "PPR", cardsOn: rostersData?.league_info?.league_format === "ppr" },
    { text: "SF", cardsOn: rostersData?.league_info?.settings?.superflex },
    { text: "TE+", cardsOn: rostersData?.league_info?.settings?.TE_Premium },
    { text: "RB PPC", cardsOn: rostersData?.league_info?.settings?.RB_PPC },
    { text: "IDP", cardsOn: false },
  ];

  const columns = React.useMemo(
    () => [
      { id: "name", label: "Name", width: "auto", align: "left" },
      { id: "team", label: "Team", width: "auto", align: "center" },
      { id: "pos", label: "Pos", width: "auto", align: "center" },
      { id: "value", label: "Value", width: "auto", align: "right" },
    ],
    []
  );
  // const headerTitle = teamDisplayName || (isMyTeamSelected ? "My Team" : "Their Team");

  // Single scroll lives in parent (RostersListDisplay Box); this is just content
  return (
    <div
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <Grid container spacing={0}>
        <Box
          textAlign="center"
          sx={{ margin: "auto", width: "150px", bgcolor: "black", border: `1px solid #999999`, borderRadius: "5px", position: "relative", zIndex: 2, top: 12 }}
        >
          <Typography variant="button" color="#999999" fontSize="12px">
            League settings
          </Typography>
        </Box>
        <Grid
          container
          spacing={0}
          margin="auto"
          mb={2}
          sx={{
            borderRadius: "5px",
            overflow: "hidden",
            border: `1px solid #999999`,
            width: "90%",
            textAlign: "center",
          }}
        >
          {informationPartners.map((infoPartner, index) => (
            <Grid key={index} item xs={2}>
              <Card
                sx={{
                  backgroundColor: `#222222`,
                  height: "50px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 0,
                  boxShadow: "none",
                  borderLeft: index !== 0 ? "0.5px dashed #999999" : "none",
                  borderRight: index !== informationPartners.length - 1 ? "0.5px dashed #999999" : "none",
                }}
              >
                <CardContent
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: 0,
                  }}
                >
                  <Typography
                    variant="h5"
                    p={1}
                    color={
                      infoPartner.cardsOn !== undefined
                        ? infoPartner.cardsOn == true
                          ? `${PureLightThemeCaaS.colors.alpha.white[100]}`
                          : `#999999`
                        : `${PureLightThemeCaaS.colors.alpha.white[100]}`
                    }
                  >
                    {infoPartner.text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          spacing={0}
          margin="auto"
          mb={2}
          sx={{
            borderRadius: "5px",
            overflow: "hidden",
            // border: `1px solid #999999`,
            width: "90%",
            textAlign: "center",
          }}
        >
          <Grid item xs={12} sx={{ margin: "0 auto" }}>
            {/* <Typography variant="h5" p={1} textAlign="center" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} className={styles.dataTitle}>
            Their Team */}
            {/* Their Team - {selectedCompetitorTeam?.team_name} */}
            {/* </Typography> */}
            {/* <Box borderTop={"1px solid white"} ml={0}></Box> */}

            <TableContainer sx={{ overflowX: "hidden" }}>
              <Table sx={{ bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}` }}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#000000",
                      borderBottom: "none",
                      backgroundImage: "linear-gradient(to top, #ffffff, #ffffff)",
                      backgroundPosition: "bottom",
                      backgroundSize: "100% 0.5px",
                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        onClick={() => column.id === "value" && handleValueSort()}
                        sx={{
                          width: column.width,
                          textAlign: column.align,
                          padding: "0px",
                          pr: 0,
                          // pl: column.id === "name" ? 0.5 : 0,
                          color: PureLightThemeCaaS.colors.alpha.white[100],
                          // borderBottom: "0.5px solid #ffffff",
                          fontSize: "10px",
                          height: "30px",
                          fontWeight: 300,
                          textTransform: "uppercase",
                          cursor: column.id === "value" ? "pointer" : "default",
                        }}
                        className={styles.dataTableTitle}
                      >
                        {column.id === "value" ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                            {column.label}
                            {valueSortDirection === "desc" ? (
                              <ArrowDropUpIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                            ) : (
                              <ArrowDropDownIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                            )}
                          </Box>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* table body for players (competitors or my team) */}
                <TableBody>
                  {sortedRows.map((row) =>
                    row.kind === "player" ? (
                      <TableRow
                        key={row.data.player_id}
                        style={{
                          // borderLeft: "2px solid #000000",
                          // borderBottom: "0.5px solid #ffffff",
                          borderBottom: "none",
                          backgroundImage: "linear-gradient(to top, #ffffff, #ffffff)",
                          backgroundPosition: "bottom",
                          backgroundSize: "100% 0.5px",
                          backgroundRepeat: "no-repeat",
                          textAlign: "center",
                          height: "40px",
                          backgroundColor: "#000000",
                        }}
                      >
                        <TableCell sx={{ p: 0, borderBottom: "none", width: "50%" }} className={styles.dataTable}>
                          <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }}>
                            {/* {getNameWithSuspensivePoints(player.name)} */}
                            {row.data.name}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{ p: 0, fontSize: "12px", textAlign: "center", color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, borderBottom: "none", width: "10%" }}
                          className={styles.dataTable}
                        >
                          {row.data.team}
                        </TableCell>
                        <TableCell
                          sx={{ p: 0, fontSize: "12px", textAlign: "center", color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, borderBottom: "none", width: "10%" }}
                          className={styles.dataTable}
                        >
                          {row.data.position}
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            fontSize: "12px",
                            fontWeight: "bold",
                            textAlign: "end",
                            pr: 0.2,
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            borderBottom: "none",
                            width: "10%",
                          }}
                          className={styles.dataTable}
                        >
                          {row.data.player_value !== undefined && row.data.player_value !== null ? formatValue(row.data.player_value) : ""}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={`draft_pick_${row.data.id}`}
                        style={{
                          // borderBottom: "0.5px solid #ffffff",
                          borderBottom: "none",
                          backgroundImage: "linear-gradient(to top, #ffffff, #ffffff)",
                          backgroundPosition: "bottom",
                          backgroundSize: "100% 0.5px",
                          backgroundRepeat: "no-repeat",
                          textAlign: "center",
                          height: "40px",
                          backgroundColor: "#000000",
                        }}
                      >
                        <TableCell
                          sx={{ p: 0, fontSize: "14px", width: "50%", color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`, borderBottom: "none", fontWeight: "bold" }}
                          className={styles.dataTable}
                        >
                          <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }}>
                            {/* {getNameWithSuspensivePoints(pick.name)} */}
                            {row.data.name}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            fontSize: "12px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            borderBottom: "none",
                            width: "10%",
                          }}
                          className={styles.dataTable}
                        >
                          -
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            fontSize: "12px",
                            textAlign: "center",
                            fontWeight: "bold",
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            borderBottom: "none",
                            width: "10%",
                          }}
                          className={styles.dataTable}
                        >
                          -
                        </TableCell>
                        <TableCell
                          sx={{
                            p: 0,
                            pr: 0.2,
                            fontSize: "12px",
                            fontWeight: "bold",
                            textAlign: "end",
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            borderBottom: "none",
                            width: "10%",
                          }}
                          className={styles.dataTable}
                        >
                          {formatValue(row.data.draft_pick_value)}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default RostersTeamView;
